import { useVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import { cm } from 'src/utils/classNames';
import { VaultStatProps, VaultStatsSkeleton } from '../VaultStatsSkeleton';
import { UIMarket } from '@drift/common';
import { UiVaults } from 'src/constants/vaults';
import {
	CAPACITY_WARNING_THRESHOLD,
	DEFAULT_PERIOD_APY,
	VAULT_APY_TOOLTIP_CONTENT,
} from 'src/constants/vaults/misc';
import {
	VAULT_APY_LABEL,
	VAULT_TARGET_APY_TOOLTIP_CONTENT,
	VAULT_TEMP_APY_LABEL,
} from 'src/constants/vaults/misc';
import { getApyToUse, getVaultApyDays } from 'src/utils/vault/math';
import { UiVaultConfig } from 'src/@types/vaults';

export const VaultStats = (props: {
	vaultPubkey: string;
	depositAssetMarketIndex: number;
}) => {
	const { data: vaultStats, isLoading: isLoadingVaultStats } = useVaultStats(
		props.vaultPubkey
	);
	const depositAssetConfig = UIMarket.createSpotMarket(
		props.depositAssetMarketIndex
	).market;
	const uiVaultConfig = UiVaults.getVaultConfig(props.vaultPubkey);

	const { apy, isUsingTemporaryApy, apyString } = getApyToUse(
		uiVaultConfig as UiVaultConfig,
		vaultStats?.apys[DEFAULT_PERIOD_APY] ?? 0,
		vaultStats?.numOfVaultSnapshots ?? 0
	);

	const stats: VaultStatProps[] = [
		{
			label: isUsingTemporaryApy
				? VAULT_TEMP_APY_LABEL
				: `${VAULT_APY_LABEL} (${getVaultApyDays(
						vaultStats?.numOfVaultSnapshots ?? 0
					)})`,
			value: apyString,
			valueClassName: cm(
				apy < 0
					? 'text-text-negative-red-button'
					: 'text-text-positive-green-button'
			),
			loading: isLoadingVaultStats,
			tooltip: isUsingTemporaryApy
				? VAULT_TARGET_APY_TOOLTIP_CONTENT
				: VAULT_APY_TOOLTIP_CONTENT,
		},
		{
			label: 'Vault Age',
			value: `${vaultStats?.numOfVaultSnapshots} days`,
			loading: isLoadingVaultStats,
		},
		{
			label: 'TVL',
			value: `${vaultStats?.tvlBase.toMillified()}`,
			marketSymbol: depositAssetConfig.symbol,
			loading: isLoadingVaultStats,
		},
		{
			label: 'Vault Capacity',
			value: vaultStats?.isUncappedCapacity
				? 'Uncapped'
				: `${vaultStats?.capacityPct.toFixed(2)}%`,
			loading: isLoadingVaultStats,
			valueClassName: cm(
				(vaultStats?.capacityPct ?? 0) >= CAPACITY_WARNING_THRESHOLD
					? 'text-star-yellow'
					: ''
			),
			tooltip: `Portion of vault's TVL quota filled. Deposits may be limited near 100%`,
		},
	];

	return <VaultStatsSkeleton stats={stats} />;
};
